/**
 * Хэширование пароля для проверки НА УСТРОЙСТВЕ.
 *
 * На сервере пароли хэшируются argon2 и оттуда не уезжают никуда и никогда.
 * Но вход оператора обязан работать без сети: смену закрывают в цеху, где связи
 * может не быть неделю. Значит, проверять пароль должно само устройство.
 *
 * Схема такая: при успешном входе ПРИ СЕТИ устройство само считает хэш от
 * введённого пароля и кладёт его в свою локальную базу. Без сети пароль
 * сверяется с этим хэшом. С сервера при этом не уезжает ни хэш, ни что-либо
 * ещё — раньше на каждое устройство рассылались хэши PIN всех операторов
 * сразу, и планшет у пресса хранил учётные данные всего цеха.
 *
 * Отсюда следствие, которое нужно понимать: войти без сети может только тот,
 * кто уже входил на этом устройстве. Первый вход каждого человека — при связи.
 *
 * PBKDF2-HMAC-SHA256, а не argon2, потому что он есть во встроенном WebCrypto
 * и в браузере, и в Node — одна и та же функция работает на обеих сторонах.
 */
export const SECRET_ITERATIONS = 210_000;
const SECRET_ALGORITHM = 'pbkdf2-sha256';
const SALT_BYTES = 16;
const KEY_BITS = 256;
function toBase64(bytes) {
    let binary = '';
    for (const byte of bytes)
        binary += String.fromCharCode(byte);
    return btoa(binary);
}
function fromBase64(value) {
    const binary = atob(value);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1)
        bytes[i] = binary.charCodeAt(i);
    return bytes;
}
async function derive(secret, salt, iterations) {
    const key = await crypto.subtle.importKey('raw', new TextEncoder().encode(secret), 'PBKDF2', false, ['deriveBits']);
    const bits = await crypto.subtle.deriveBits({ name: 'PBKDF2', salt: salt, iterations, hash: 'SHA-256' }, key, KEY_BITS);
    return new Uint8Array(bits);
}
export async function hashSecret(secret, iterations = SECRET_ITERATIONS) {
    if (secret.length === 0)
        throw new Error('Пустой пароль хэшировать нечего');
    const salt = crypto.getRandomValues(new Uint8Array(SALT_BYTES));
    const hash = await derive(secret, salt, iterations);
    return `${SECRET_ALGORITHM}$${iterations}$${toBase64(salt)}$${toBase64(hash)}`;
}
/** Сравнение за постоянное время: длина одинаковая, ранний выход не делаем. */
function equalBytes(a, b) {
    if (a.length !== b.length)
        return false;
    let diff = 0;
    for (let i = 0; i < a.length; i += 1)
        diff |= (a[i] ?? 0) ^ (b[i] ?? 0);
    return diff === 0;
}
export async function verifySecret(secret, stored) {
    if (secret.length === 0)
        return false;
    const parts = stored.split('$');
    if (parts.length !== 4 || parts[0] !== SECRET_ALGORITHM)
        return false;
    const iterations = Number.parseInt(parts[1] ?? '', 10);
    if (!Number.isInteger(iterations) || iterations <= 0)
        return false;
    try {
        const salt = fromBase64(parts[2] ?? '');
        const expected = fromBase64(parts[3] ?? '');
        const actual = await derive(secret, salt, iterations);
        return equalBytes(actual, expected);
    }
    catch {
        return false;
    }
}
//# sourceMappingURL=secret.js.map